import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
  Typography,
  Table, TableHead, TableBody, TableRow, TableCell
} from '@one-thd/sui-atomic-components';
import {
  QueryProvider,
  extend
} from '@thd-nucleus/data-sources';
import { useStore } from '@thd-nucleus/experience-context';
import { QuickEstimateItem } from './QuickEstimateItem';
import { useProductBatchLoader } from '../../hooks/useProductBatchLoader';
import { useSubtotalFetcher } from '../../hooks/useSubtotalFetcher';
import { QuickEstimateItemPlaceholder } from './QuickEstimateItemPlaceholder';

export const QuickEstimateGroup = React.memo(({
  group,
  index,
  isResolvingIntents,
  updateGroupTotal
}) => {
  const { storeId, membershipInformation } = useStore();
  const { groupName, items } = group;
  const [expanded, setExpanded] = React.useState(index === 0);

  const loyaltyMembershipInput = useMemo(() => {
    return membershipInformation?.data?.loyaltyMembership || null;
  }, [membershipInformation?.data?.loyaltyMembership]);

  const { groupTotal, subtotalError } = useSubtotalFetcher({
    items,
    storeId,
    isResolvingIntents
  });

  // Use custom hooks to handle product loading and subtotal fetching
  const { loadedItemIds } = useProductBatchLoader({
    items,
    storeId,
    loyaltyMembershipInput,
    skip: !expanded
  });

  useEffect(() => {
    if (groupTotal !== null) {
      updateGroupTotal(groupName, groupTotal);
    }
  }, [groupTotal]);

  const onExpandedChange = () => {
    setExpanded((prev) => !prev);
  };

  if (isResolvingIntents) {
    return null;
  }

  return (
    <div className="sui-w-full sui-mb-2">
      <Accordion noBorders expanded={expanded} onChange={onExpandedChange}>
        <div
          className="sui-flex sui-items-center sui-bg-subtle"
        >
          <div className="sui-w-full">
            <AccordionHeader
              aria-controls={`quick-estimate-group-${index}`}
              id={`quick-estimate-group-header-${index}`}
              style={{ paddingLeft: '10px', paddingRight: '10px', backgroundColor: 'inherit' }}
            >
              {groupName || 'Ungrouped Items'}
            </AccordionHeader>
          </div>
        </div>
        <AccordionBody>
          {
            expanded && (
              <div className="sui-px-2 sui-pb-2">
                {items?.length > 0 ? (
                  <Table unlined>
                    <TableHead className="sui-w-full">
                      <TableRow>
                        <TableCell className="sui-text-left sui-px-1 sui-py-2">Item</TableCell>
                        <TableCell className="sui-text-left sui-px-1 sui-py-2">Qty</TableCell>
                        <TableCell className="sui-text-left sui-px-2 sui-py-2">Unit Price</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>

                      {
                        items.map((item, itemIndex) => {
                          if (item.itemId && !loadedItemIds.includes(item.itemId)) {
                            return (
                              <QuickEstimateItemPlaceholder
                                key={`groupItem-placeholder-${index}-${itemIndex}`}
                                itemIndex={itemIndex}
                              />
                            );
                          }

                          return (
                            <QueryProvider
                              key={`groupItem-${index}-${itemIndex}`}
                              cacheKey="QE-products"
                              defaultVariables={{
                                storeId,
                                isBrandPricingPolicyCompliant: true,
                                loyaltyMembershipInput
                              }}
                              dataSource="catalog"
                            >
                              <QuickEstimateItem
                                item={item}
                                groupName={groupName}
                                groupIndex={index}
                                itemIndex={itemIndex}
                              />
                            </QueryProvider>
                          );
                        })
                      }
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body-sm" color="subtle">
                    No items in this group
                  </Typography>
                )}
              </div>
            )
          }
        </AccordionBody>
      </Accordion>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.group === nextProps.group
    && prevProps.index === nextProps.index
    && prevProps.isResolvingIntents === nextProps.isResolvingIntents
  );
});

QuickEstimateGroup.displayName = 'QuickEstimateGroup';

QuickEstimateGroup.propTypes = {
  group: PropTypes.shape({
    groupName: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      itemId: PropTypes.string,
      intentId: PropTypes.string,
      quantity: PropTypes.number,
      intent: PropTypes.string
    }))
  }).isRequired,
  isResolvingIntents: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  updateGroupTotal: PropTypes.func.isRequired
};

QuickEstimateGroup.defaultProps = {
};

// Initialize dataModel with priceSubtotal
QuickEstimateGroup.dataModel = extend(
  useSubtotalFetcher,
  useProductBatchLoader
);
